/**
 * What each admin-panel role may open. The backend is the real gate (middleware/requireAdminScope.js,
 * utils/adminScopes.js in the backend): every admin API checks the role, and shared lists (rides,
 * drivers, users, tickets, ratings) are filtered to the login's services there. This file only decides
 * what the panel shows, so a login never lands on a page that would just fail.
 *
 * A login may hold several roles (backend migration 244); it can open the union of their pages.
 */

/** Pages (and their sub-routes) a marketing login can open. */
const MARKETING_PATHS = ['/marketing', '/website-leads'];

/** Finance (backend migration 246): money across all services. Keep in step with the backend's financeAccess routes. */
const FINANCE_PATHS = [
    '/transactions', '/wallet', '/withdrawals',
    '/bills', '/billing-customers', '/billing-items', '/billing-settings',
    '/commute-payout-batches', '/commute-driver-ledger',
];

/** Shared pages; the backend filters their rows to the login's services. */
const RIDE_SHARED = ['/rides', '/drivers', '/users', '/tickets', '/ratings'];

/** Pages per operations role. Keep in step with the backend's adminScopes / adminRoutes SCOPED_PREFIXES. */
const OPS_PATHS: Record<string, string[]> = {
    ops_normal: ['/scheduled-rides', ...RIDE_SHARED],
    ops_rentals: [
        '/scheduled-rides', '/rental-rides', '/rental-packages', '/rental-rules', '/rental-surcharges', '/rental-disputes',
        '/outstation-settings', '/outstation-bookings', '/outstation-fares', '/outstation-rides', '/outstation-charges',
        ...RIDE_SHARED,
    ],
    ops_commute: [
        '/commute-routes', '/commute-trips', '/commute-waitlist', '/requests-demand', '/commute-bookings', '/commute-passes',
        '/cancellations', '/commute', '/commute-salary-config', '/commute-incentive-rules', '/commute-attendance',
        '/commute-penalties', '/commute-payout-batches', '/commute-driver-ledger',
        ...RIDE_SHARED,
    ],
    ops_coach: ['/coach-bookings', '/coach-operations', '/coach-listings', '/coach-settings', '/vehicle-booking-requests', '/drivers', '/users', '/ratings'],
    ops_wedding: ['/wedding-bookings', '/wedding-availability', '/wedding-pricing', '/wedding-settings', '/drivers', '/users'],
};

export const ROLE_LABELS: Record<string, string> = {
    super_admin: 'Super Admin',
    marketing: 'Marketing',
    ops_normal: 'Normal Operations',
    ops_rentals: 'Rentals',
    ops_commute: 'Commute',
    ops_coach: 'Coach',
    ops_wedding: 'Car Hire for Wedding',
    finance: 'Finance',
};

type Roles = string | string[] | null | undefined;
const list = (r: Roles): string[] => (Array.isArray(r) ? r : r ? [r] : []);

const tokenPayload = (token: string | null): any => {
    if (!token) return null;
    try {
        return JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    } catch {
        return null;
    }
};

/** Primary role signed into the admin JWT by the server (logins from before roles existed carry 'super_admin'). */
export const roleFromToken = (token: string | null): string | null => {
    const role = tokenPayload(token)?.role;
    return typeof role === 'string' ? role : null;
};

/** Every role of the login (tokens from before multi-role logins carry only `role`). */
export const rolesFromToken = (token: string | null): string[] => {
    const p = tokenPayload(token);
    if (Array.isArray(p?.roles) && p.roles.length) return p.roles.filter((r: unknown) => typeof r === 'string');
    return typeof p?.role === 'string' ? [p.role] : [];
};

export const isSuperAdmin = (roles: Roles): boolean => list(roles).includes('super_admin');

/** Approving / paying Commute payout batches and ledger adjustments: Super Admin and Finance (the backend checks too). */
export const canMoveMoney = (roles: Roles): boolean => isSuperAdmin(roles) || list(roles).includes('finance');

const allowedPaths = (roles: Roles): string[] => list(roles).flatMap((r) => (r === 'marketing' ? MARKETING_PATHS : r === 'finance' ? FINANCE_PATHS : OPS_PATHS[r] || []));

const matches = (p: string, pathname: string) => pathname === p || pathname.startsWith(`${p}/`);

/** Unknown roles get the narrowest access. */
export const canOpenPath = (roles: Roles, pathname: string): boolean =>
    isSuperAdmin(roles) || allowedPaths(roles).some((p) => matches(p, pathname));

/**
 * Where a role lands after login, and when it opens a page it can't use. Only super admin uses the
 * module picker; every other login gets one sidebar with all its pages (DashboardLayout).
 */
export const homePathFor = (roles: Roles): string => (isSuperAdmin(roles) ? '/categories' : allowedPaths(roles)[0] || '/login');

/** Sidebar order for non-super logins: their own service's pages first, shared pages after. */
export const menuRank = (roles: Roles, path: string): number => {
    const i = allowedPaths(roles).findIndex((p) => matches(p, path));
    return i < 0 ? Number.MAX_SAFE_INTEGER : i;
};

/** Label for the header: one role by name, several as "Coach + Car Hire for Wedding". */
export const rolesLabel = (roles: Roles): string =>
    list(roles).map((r) => ROLE_LABELS[r] ?? r).join(' + ') || 'Admin';
